import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYER_META = {
  operacion: { label: "En la operación", color: "#0033AD" },
  producto: { label: "En el producto terminado", color: "#FFC400" },
  consumidor: { label: "En el consumidor", color: "#E0162B" },
  negocio: { label: "En el negocio", color: "#11142B" },
  ambiental: { label: "Ambiental", color: "#2A8A3E" },
  social: { label: "Social", color: "#7BAE3F" },
  gobierno: { label: "Gobierno corporativo", color: "#0033AD" },
};

/**
 * Muestra al proveedor, por pilar, cómo su desempeño impacta el negocio Lala en 4 capas
 * (Operación · Producto · Consumidor · Negocio). Refleja el copy oficial enviado por Lala.
 *
 * Cuando se pasa `pillarKey`, muestra solo ese pilar. Si no, los muestra todos colapsables.
 */
export default function BusinessImpactPanel({ pillarKey }) {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(pillarKey || null);

  useEffect(() => {
    fetch(`${API}/public/pillar-narratives`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data?.pillars?.length) return null;
  const pillars = pillarKey ? data.pillars.filter((p) => p.key === pillarKey) : data.pillars;

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="business-impact-panel">
      <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
        <TrendingUp size={18} /> cómo tu desempeño impacta el negocio Lala
      </div>
      <h3 className="font-display text-2xl mb-3">Tu trabajo, multiplicado en cuatro capas.</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-5 max-w-3xl">
        Cada uno de tus indicadores se traduce en impacto real para operación, producto, consumidor y negocio.
        Aquí lo ves explícito — porque cuando creces, todos crecemos.
      </p>

      <div className="space-y-3">
        {pillars.map((p) => {
          const isOpen = openKey === p.key;
          const layers = Object.entries(p.business_impact || {});
          return (
            <div key={p.key} className="bg-white border border-[var(--line)] rounded-xl overflow-hidden" data-testid={`impact-pillar-${p.key}`}>
              <button onClick={() => setOpenKey(isOpen ? null : p.key)} className="w-full px-4 py-3 flex items-start justify-between gap-3 text-left hover:bg-[var(--cream-soft)] transition-colors">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--lala-blue)] font-bold">{p.label}</div>
                  {p.owner_area && (
                    <div className="text-[10px] text-[var(--ink-soft)] mt-0.5" data-testid={`pillar-owner-${p.key}`}>
                      Área responsable · <b>{p.owner_area}</b>
                    </div>
                  )}
                  <div className="font-display text-lg leading-tight mt-0.5">{p.tagline}</div>
                  <div className="font-hand text-base text-[var(--lala-blue)] mt-1 leading-tight">“{p.campaign_phrase}”</div>
                </div>
                <div className="text-xs text-[var(--ink-soft)]">{isOpen ? "−" : "+"}</div>
              </button>
              {isOpen && (
                <div className="px-4 py-3 border-t border-[var(--line)] space-y-3">
                  <p className="text-sm text-[var(--ink)] leading-relaxed">
                    <strong>Lo que buscamos:</strong> {p.what_we_seek}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {layers.map(([key, items]) => {
                      const meta = LAYER_META[key] || { label: key, color: "#9AA1BD" };
                      return (
                        <div key={key} className="bg-[var(--cream-soft)] border border-[var(--line)] rounded-lg p-3" data-testid={`impact-layer-${p.key}-${key}`}>
                          <div className="text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: meta.color }}>
                            {meta.label}
                          </div>
                          <ul className="space-y-1">
                            {items.map((it, idx) => (
                              <li key={idx} className="text-xs text-[var(--ink)] leading-snug flex items-start gap-1.5">
                                <span className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: meta.color }} />
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  {p.executive_message && (
                    <div className="bg-[var(--lala-blue)] text-white rounded-lg p-3 text-sm leading-relaxed">
                      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold mb-1">mensaje ejecutivo</div>
                      {p.executive_message}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
